import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Consent, ConsentTemplate, LoaderError } from '../../model';
import { UserConsentFacade } from '../facades';
import { UserConsentService } from './user-consent.service';

/**
 * This is needed to convert non-observable return values to observables, so that we can use it as a Spartacus proxy facade.
 */
@Injectable()
export class UserConsentWrapperService implements UserConsentFacade {
  constructor(private userConsentService: UserConsentService) {}

  resetConsentsProcessState(): void {
    return this.userConsentService.resetConsentsProcessState();
  }

  filterConsentTemplates(templateList: ConsentTemplate[], hideTemplateIds?: string[]): Observable<ConsentTemplate[]> {
    return of(this.userConsentService.filterConsentTemplates(templateList, hideTemplateIds));
  }

  loadConsents(): void {
    return this.userConsentService.loadConsents();
  }

  getConsents(loadIfMissing?: boolean): Observable<ConsentTemplate[]> {
    return this.userConsentService.getConsents(loadIfMissing);
  }

  getRequiredConsentsNotGiven(): Observable<Consent[]> {
    return this.userConsentService.getRequiredConsentsNotGiven();
  }

  getOptionalConsents(): Observable<ConsentTemplate[]> {
    return this.userConsentService.getOptionalConsents();
  }

  getConsentsResultLoading(): Observable<boolean> {
    return this.userConsentService.getConsentsResultLoading();
  }

  getConsentsResultSuccess(): Observable<boolean> {
    return this.userConsentService.getConsentsResultSuccess();
  }

  getConsentsResultErrorDetails(): Observable<LoaderError | undefined> {
    return this.userConsentService.getConsentsResultErrorDetails();
  }

  getConsent(templateId: string): Observable<Consent | undefined> {
    return this.userConsentService.getConsent(templateId);
  }

  giveConsent(consentTemplateId: string, consentTemplateVersion: number): void {
    return this.userConsentService.giveConsent(consentTemplateId, consentTemplateVersion);
  }

  getGiveConsentResultLoading(): Observable<boolean> {
    return this.userConsentService.getGiveConsentResultLoading();
  }

  getGiveConsentResultSuccess(): Observable<boolean> {
    return this.userConsentService.getGiveConsentResultSuccess();
  }

  getGiveConsentResultErrorDetails(): Observable<LoaderError | undefined> {
    return this.userConsentService.getGiveConsentResultErrorDetails();
  }

  withdrawConsent(consentCode: string): void {
    return this.userConsentService.withdrawConsent(consentCode);
  }

  denyConsent(consentTemplateId: string, consentTemplateVersion: number): void {
    return this.userConsentService.denyConsent(consentTemplateId, consentTemplateVersion);
  }

  getWithdrawConsentResultLoading(): Observable<boolean> {
    return this.userConsentService.getWithdrawConsentResultLoading();
  }

  getWithdrawConsentResultSuccess(): Observable<boolean> {
    return this.userConsentService.getWithdrawConsentResultSuccess();
  }

  getWithdrawConsentResultErrorDetails(): Observable<LoaderError | undefined> {
    return this.userConsentService.getWithdrawConsentResultErrorDetails();
  }

  isTrackingAllowed(): Observable<boolean> {
    return this.userConsentService.isTrackingAllowed();
  }

  isPersonalizationAllowed(): Observable<boolean> {
    return this.userConsentService.isPersonalizationAllowed();
  }

  isMarketingThirdPartyAllowed(): Observable<boolean> {
    return this.userConsentService.isMarketingThirdPartyAllowed();
  }

  isBannerVisible(): Observable<boolean> {
    return this.userConsentService.isBannerVisible();
  }

  isSettingsVisible(): Observable<boolean> {
    return this.userConsentService.isSettingsVisible();
  }

  toggleBannerVisible(dismissed: boolean): void {
    return this.userConsentService.toggleBannerVisible(dismissed);
  }

  toggleSettingsModalVisible(visible: boolean, showCloseButton?: boolean): void {
    return this.userConsentService.toggleSettingsModalVisible(visible, showCloseButton);
  }
}
